import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Deviation lifecycle surface (T-0068 / E06-F06).
 *
 * The deviations screen consumes a cursor-paginated list endpoint plus an
 * optional per-deviation detail (used by the inline expand-rationale UI).
 * Filters supported by the backend:
 *   - status:        open | in_resolution | resolved | waived
 *   - severity:      Critical | High | Medium | Low
 *   - deviation_type: missed_milestone | unmet_kpi | quality | sla_breach
 *   - deliverable_entry_id: scope to a deliverable
 *
 * Lifecycle mutations (create / transition) land on T-0114 / T-0117 modals
 * — this client only exposes the read surface needed for T-0103.
 */

export const DEVIATION_STATUSES = ['open', 'in_resolution', 'resolved', 'waived'] as const;
export type DeviationStatus = (typeof DEVIATION_STATUSES)[number];

export const DEVIATION_SEVERITIES = ['Critical', 'High', 'Medium', 'Low'] as const;
export type DeviationSeverity = (typeof DEVIATION_SEVERITIES)[number];

export const DEVIATION_TYPES = ['missed_milestone', 'unmet_kpi', 'quality', 'sla_breach'] as const;
export type DeviationType = (typeof DEVIATION_TYPES)[number];

export const DeviationSummarySchema = z.object({
  deviation_entry_id: z.string().uuid(),
  title: z.string(),
  status: z.string(),
  severity: z.string(),
  escalation_due: z.string().nullable().optional(),
  escalation_status: z.string().nullable().optional(),
  escalation_breached_at: z.string().nullable().optional(),
  // 'system' when a detection rule raised the deviation automatically.
  detected_by: z.string().nullable().optional(),
  detected_rule: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  checkpoint_id: z.string().nullable().optional(),
  checkpoint_name: z.string().nullable().optional(),
  source_document_id: z.string().nullable().optional(),
  source_document_title: z.string().nullable().optional(),
  source_page_number: z.number().nullable().optional(),
  deviation_type: z.string(),
  deliverable_entry_id: z.string().uuid().nullable(),
  /** The linked deliverable's words (C2-31); optional so an older server still parses. */
  deliverable_title: z.string().nullable().optional(),
  deliverable_sow_ref: z.string().nullable().optional(),
  /** Why it was waived; set only on waived rows (C2-19). */
  waiver_reason: z.string().nullable().optional(),
  created_at: z.string(),
});
export type DeviationSummary = z.infer<typeof DeviationSummarySchema>;

export const DeviationListSchema = z.object({
  items: z.array(DeviationSummarySchema),
  next_cursor: z.string().nullable(),
});
export type DeviationList = z.infer<typeof DeviationListSchema>;

export const DeviationTransitionSchema = z.object({
  id: z.string().uuid(),
  from_status: z.string().nullable(),
  to_status: z.string(),
  actor_user_id: z.string().nullable(),
  note: z.string().nullable(),
  created_at: z.string(),
});
export type DeviationTransition = z.infer<typeof DeviationTransitionSchema>;

export const DeviationDetailSchema = z.object({
  deviation_entry_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  title: z.string(),
  status: z.string(),
  severity: z.string(),
  deviation_type: z.string(),
  resolution_notes: z.string().nullable(),
  waiver_reason: z.string().nullable(),
  deliverable_title: z.string().nullable().optional(),
  deliverable_sow_ref: z.string().nullable().optional(),
  ai_run_id: z.string().nullable(),
  origin: z.string(),
  original_ai_confidence: z.number().nullable(),
  deliverable_entry_id: z.string().uuid().nullable(),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  days_in_state: z.number(),
  transitions: z.array(DeviationTransitionSchema),
});
export type DeviationDetail = z.infer<typeof DeviationDetailSchema>;

export interface ListDeviationsOpts {
  status?: DeviationStatus;
  severity?: DeviationSeverity;
  deviation_type?: DeviationType;
  deliverable_entry_id?: string;
  cursor?: string;
  limit?: number;
}

export function listDeviations(
  engagementId: string,
  opts: ListDeviationsOpts = {},
): Promise<DeviationList> {
  return apiClient(`/api/v1/contract/deviations`, DeviationListSchema, {
    query: {
      engagement_id: engagementId,
      ...(opts.status ? { status: opts.status } : {}),
      ...(opts.severity ? { severity: opts.severity } : {}),
      ...(opts.deviation_type ? { deviation_type: opts.deviation_type } : {}),
      ...(opts.deliverable_entry_id ? { deliverable_entry_id: opts.deliverable_entry_id } : {}),
      ...(opts.cursor ? { cursor: opts.cursor } : {}),
      ...(opts.limit ? { limit: opts.limit } : {}),
    },
  });
}

export function useDeviationsListQuery(
  engagementId: string | undefined,
  opts: ListDeviationsOpts = {},
) {
  return useQuery({
    queryKey: ['deviations', engagementId, opts] as const,
    queryFn: () => listDeviations(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

export function getDeviation(deviationEntryId: string): Promise<DeviationDetail> {
  return apiClient(
    `/api/v1/contract/deviations/${encodeURIComponent(deviationEntryId)}`,
    DeviationDetailSchema,
  );
}

export function useDeviationDetailQuery(deviationEntryId: string | undefined) {
  return useQuery({
    queryKey: ['deviation', deviationEntryId] as const,
    queryFn: () => getDeviation(deviationEntryId!),
    enabled: Boolean(deviationEntryId),
  });
}

// ===========================================================================
// Deviation create + transition (Tier 2)
// ===========================================================================

/** Mirror of the API's legal transition map (services/deviations.ts). */
export const DEVIATION_LEGAL_TRANSITIONS: Record<string, readonly string[]> = {
  open: ['in_resolution', 'resolved', 'waived'],
  in_resolution: ['resolved', 'waived', 'open'],
  resolved: [],
  waived: [],
};

const DeviationCreateResultSchema = z
  .object({ deviation_entry_id: z.string(), status: z.string(), severity: z.string() })
  .passthrough();
const DeviationTransitionResultSchema = z
  .object({ deviation_entry_id: z.string(), status: z.string(), transitioned_at: z.string() })
  .passthrough();

export interface CreateDeviationBody {
  engagement_id: string;
  title: string;
  severity: string;
  deviation_type: string;
  deliverable_entry_id?: string | null;
}

export function useCreateDeviationMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: CreateDeviationBody;
      idempotencyKey?: string;
    }) =>
      apiClient(`/api/v1/contract/deviations`, DeviationCreateResultSchema, {
        method: 'POST',
        body,
        ...(idempotencyKey ? { idempotencyKey } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['deviations', engagementId] });
    },
  });
}

export interface TransitionDeviationBody {
  to_status: string;
  note?: string | null;
  waiver_reason?: string | null;
  evidence_link_ids?: string[] | null;
}

export function useTransitionDeviationMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      deviationEntryId,
      body,
      idempotencyKey,
    }: {
      deviationEntryId: string;
      body: TransitionDeviationBody;
      idempotencyKey?: string;
    }) =>
      apiClient(
        `/api/v1/contract/deviations/${encodeURIComponent(deviationEntryId)}/transition`,
        DeviationTransitionResultSchema,
        { method: 'POST', body, ...(idempotencyKey ? { idempotencyKey } : {}) },
      ),
    onSuccess: (_result, { deviationEntryId }) => {
      void qc.invalidateQueries({ queryKey: ['deviations', engagementId] });
      // The card's open history reads the detail; refresh it with the new step.
      void qc.invalidateQueries({ queryKey: ['deviation', deviationEntryId] });
    },
  });
}

const AcknowledgeResultSchema = z
  .object({ deviation_entry_id: z.string(), escalation_status: z.string() })
  .passthrough();

export function useAcknowledgeEscalationMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviationEntryId: string) =>
      apiClient(
        `/api/v1/contract/deviations/${encodeURIComponent(deviationEntryId)}/acknowledge`,
        AcknowledgeResultSchema,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['deviations', engagementId] });
    },
  });
}

/** Overdue = SLA passed, not acknowledged, deviation still open-ish. */
export function isEscalationOverdue(d: DeviationSummary, now = Date.now()): boolean {
  return Boolean(
    d.escalation_due &&
    d.escalation_status === 'pending' &&
    (d.status === 'open' || d.status === 'in_resolution') &&
    new Date(d.escalation_due).getTime() < now,
  );
}
